import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CInputRadio,
  CLabel,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CTabContent,
  CTabPane,
  CTabs,
} from "@coreui/react";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  updateItem,
  ItemRequestStatus,
  getList,
  createItem,
  getItem,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, successAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

import moment from "moment";
import StoreExpense, { newStoreExpense } from "../../models/store-expense";
import CurrencyField from "../currencies/CurrencyField";
import { PYG } from "../../currency/available-currencies";

import { formatToCurrency } from "../../currency/format";

import SupplierSelect from "../supplier/SupplierSelect";
import Supplier from "../../models/supplier";
import AccountingEntryFormItem from "../accounting-entries-groups/AccountingEntryFormItem";
import {
  listToUniqueListItems,
  newUniqueListItem,
  UniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";
import AccountingEntry from "../../models/accounting-entries";
import AccountingEntriesGroup from "../../models/accounting-entries-group";
import { BsFillPlusCircleFill } from "react-icons/bs";

interface StoreExpenseFormProps {
  initialStoreExpense?: StoreExpense;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const StoreExpenseAddInvoiceForm: React.FC<StoreExpenseFormProps> = ({
  initialStoreExpense,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingItem, setEditingItem] = useState<StoreExpense>(
    initialStoreExpense ? initialStoreExpense : newStoreExpense()
  );

  const [selectedSupplier, setSelectedSupplier] = useState<
    Supplier | undefined
  >(undefined);
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const logoModified = useRef<boolean>(false);

  const [items, setItems] = useState<UniqueListItem<AccountingEntry>[]>([]);

  const onInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      invoiceNumber: e.target.value,
    });
  };

  const onSupplierNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      supplierName: e.target.value,
    });
  };

  const onStampChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      stamp: e.target.value,
    });
  };

  const onSupplierRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      supplierRuc: e.target.value,
    });
  };

  const onSupplierChange = (newSupplier: Supplier | null) => {
    setSelectedSupplier(newSupplier !== null ? newSupplier : undefined);
    setEditingItem({
      ...editingItem,
      supplierId: newSupplier !== null ? newSupplier.id : undefined,
      supplierName:
        newSupplier !== null
          ? newSupplier.legalName || newSupplier.name
          : undefined,
      supplierRuc: newSupplier !== null ? newSupplier.ruc : undefined,
    });
  };

  const onIVA5Change = (newAmount: number | undefined) => {
    setEditingItem({
      ...editingItem,
      iva5: newAmount,
    });
  };

  const onIVAExemptChange = (newAmount: number | undefined) => {
    setEditingItem({
      ...editingItem,
      ivaExempt: newAmount,
    });
  };

  const onIVA10Change = (newAmount: number | undefined) => {
    setEditingItem({
      ...editingItem,
      iva10: newAmount,
    });
  };

  const onExpenseInvoiceTypeChange = (newExpenseInvoiceType: string) => {
    setEditingItem({
      ...editingItem,
      expenseInvoiceType: newExpenseInvoiceType,
    });
  };

  const onAddSupplierClick = async () => {
    setSubmitting(true);
    let toSendSupplier: Supplier = {
      companyId: company.id,
      name: editingItem.supplierName,
      legalName: editingItem.supplierName,
      ruc: editingItem.supplierRuc,
    };
    let requestPromise: Promise<ItemRequestStatus<Supplier>>;
    if (toSendSupplier.id === undefined) {
      requestPromise = createItem<Supplier>(
        "/suppliers/create/",
        toSendSupplier
      );
    } else {
      requestPromise = updateItem<Supplier>(
        `/suppliers/${toSendSupplier.id}/`,
        toSendSupplier
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      setSelectedSupplier(requestStatus.data);
      setEditingItem({
        ...editingItem,
        supplierId: requestStatus.data?.id,
        supplierName: requestStatus.data?.legalName,
        supplierRuc: requestStatus.data?.ruc,
      });
      successAlert(
        "Proveedor creado exitosamente. ya puede encontrar su cuenta contable en la lista"
      );
    }
    setSubmitting(false);
  };

  const onSave = async () => {
    setSubmitting(true);
    const toSendItems: AccountingEntry[] = uniqueListToListItems(items);

    toSendItems.forEach((item) => {
      item.companyId = company.id;
      item.status = "completed";

      item.date = editingItem.expenseDate;
      item.status = "created";
      if (!item.creditAmount) {
        item.creditAmount = 0;
      }
      if (!item.debitAmount) {
        item.debitAmount = 0;
      }
      if (!item.associatedInvoiceNumber) {
        item.associatedInvoiceNumber = editingItem.invoiceNumber;
      }
      if (!item.associatedRuc) {
        item.associatedRuc = editingItem.supplierRuc;
      }
      if (!item.associatedTaxPayer) {
        item.associatedTaxPayer = editingItem.supplierName;
      }
    });

    let accountData: AccountingEntriesGroup = {
      ...editingItem.accountingGroup,
      status: "created",
      companyId: company.id,
      date: editingItem.expenseDate,
      items: toSendItems,
    };
    let toSendExpense: StoreExpense = {
      ...editingItem,
      companyId: company.id,
      accountingGroup: accountData,
    };

    const operationDate = moment().toISOString();

    let formErrors: Record<string, string[]> = {};

    if (toSendExpense.expenseType === undefined) {
      toSendExpense.expenseType = "in_factory";
    }
    if (toSendExpense.expenseInvoiceType === undefined) {
      toSendExpense.expenseInvoiceType = "cash";
    }

    if (toSendExpense.description === "") {
      toSendExpense.description = undefined;
    }
    if (toSendExpense.revertDescription === "") {
      toSendExpense.revertDescription = undefined;
    }

    let requestPromise: Promise<ItemRequestStatus<StoreExpense>>;
    if (editingItem.id === undefined) {
      errorAlert("No se pudo encontrar gasto");
      setSubmitting(false);
      return;
    } else {
      if (toSendExpense.expenseDate === undefined) {
        toSendExpense.expenseDate = operationDate;
      }
      const sueldosYJornalesEntry = toSendItems.find(
        (item) => item.account?.name === "SUELDOS Y JORNALES"
      );
      toSendExpense.hasLegalInvoice = true;
      if (toSendExpense.hasLegalInvoice) {
        if (
          !toSendExpense.invoiceNumber &&
          sueldosYJornalesEntry?.account?.name !== "SUELDOS Y JORNALES"
        ) {
          formErrors.invoiceNumber = ["Debe indicar el número de factura"];
        }
        if (
          !toSendExpense.supplierName &&
          sueldosYJornalesEntry?.account?.name !== "SUELDOS Y JORNALES"
        ) {
          formErrors.supplierName = ["Seleccione un proveedor"];
        }
        if (
          !toSendExpense.supplierRuc &&
          sueldosYJornalesEntry?.account?.name !== "SUELDOS Y JORNALES"
        ) {
          formErrors.supplierRuc = ["Seleccione un proveedor"];
        }
        if (
          (toSendExpense.iva10 || 0) +
            (toSendExpense.iva5 || 0) +
            (toSendExpense.ivaExempt || 0) !==
          toSendExpense.price
        ) {
          formErrors.iva10 = ["Los montos de IVA no suman el total"];
          formErrors.iva5 = ["Los montos de IVA no suman el total"];
          formErrors.ivaExempt = ["Los montos de IVA no suman el total"];
        }

        if (!toSendExpense.expenseInvoiceType) {
          formErrors.expenseInvoiceType = ["Seleccione una condición de venta"];
        }
        if (Object.keys(formErrors).length > 0) {
          setErrors(formErrors);
          setSubmitting(false);
          return;
        }
        if (toSendItems.length === 0) {
          errorAlert("El asiento contable debe tener al menos una entrada");
          setSubmitting(false);
          return;
        }
      }
      if (toSendItems.length === 0) {
        toSendExpense.accountingGroup = undefined;
      }
      requestPromise = updateItem<StoreExpense>(
        `/store_expenses/${toSendExpense.id}/`,
        toSendExpense
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingItem(newStoreExpense());
    setSelectedSupplier(undefined);
    setItems([]);
    setErrors({});

    logoModified.current = false;
  };

  const onAddEntryClick = () => {
    setItems((items) => {
      const initialValue: AccountingEntry = {};
      if (editingItem.invoiceNumber) {
        initialValue.subject = `Factura Nro: ${editingItem.invoiceNumber}`;
      }
      const newItems = [
        ...items,
        newUniqueListItem<AccountingEntry>(initialValue),
      ];
      return newItems;
    });
  };

  const onEntryChange = (entry: UniqueListItem<AccountingEntry>) => {
    setItems((items) => {
      const newItems = [...items];
      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems[index] = entry;

      return newItems;
    });
  };

  const onEntryDelete = (entry: UniqueListItem<AccountingEntry>) => {
    setItems((items) => {
      const newItems = [...items];

      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems.splice(index, 1);

      return newItems;
    });
  };

  const fetchItems = async (
    initialAccountingEntriesGroup: AccountingEntriesGroup | undefined
  ) => {
    if (
      initialAccountingEntriesGroup === undefined ||
      initialAccountingEntriesGroup.id === undefined
    ) {
      setItems([]);
      return [];
    }
    const groupIdStr = initialAccountingEntriesGroup.id.toString();
    if (groupIdStr === undefined) {
      setItems([]);
      return [];
    }
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("group_id", groupIdStr);
    const limit = 100;
    const offset = 0;
    const itemsStatus = await getList<AccountingEntry>(
      `/accounting_entries/`,
      limit,
      offset,
      additionalParams
    );
    if (itemsStatus.status === SUCCESS) {
      if (itemsStatus.data !== undefined) {
        setItems(listToUniqueListItems(itemsStatus.data.items));
      }
    } else {
      setItems([]);
    }
  };

  const fetchSupplier = async (id: number) => {
    const supplierStatus = await getItem<Supplier>(`/suppliers/${id}/`);
    if (supplierStatus.status === SUCCESS) {
      if (supplierStatus.data !== undefined) {
        console.log(supplierStatus.data);
        setSelectedSupplier(supplierStatus.data);
      }
    }
  };

  useEffect(() => {
    setEditingItem(
      initialStoreExpense ? initialStoreExpense : newStoreExpense()
    );
    if (initialStoreExpense?.supplierId) {
      fetchSupplier(initialStoreExpense.supplierId);
    }
    fetchItems(initialStoreExpense?.accountingGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStoreExpense]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  const itemErrors = getFieldErrors("items", errors) as Errors[];

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Sucursal:</CLabel>
            </CCol>
            <CCol>
              <p className="mt-2">{editingItem.storeName}</p>
            </CCol>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Usuario:</CLabel>
            </CCol>
            <CCol>
              <p className="mt-2">{editingItem.userName}</p>
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Concepto:</CLabel>
            </CCol>
            <CCol>
              <p className="mt-2">{editingItem.description}</p>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Este pago:</CLabel>
            </CCol>
            <CCol>
              <p className="mt-2">
                {formatToCurrency(editingItem.price || 0, PYG)}
              </p>
            </CCol>
          </CRow>
        </CFormGroup>
        <CTabs activeTab="invoice">
          <CNav variant="tabs">
            <CNavItem>
              <CNavLink data-tab="invoice">Datos de Factura/pago</CNavLink>
            </CNavItem>

            <CNavItem>
              <CNavLink data-tab="accounts">Datos de Asiento Contable</CNavLink>
            </CNavItem>
          </CNav>
          <CTabContent>
            <CTabPane data-tab="invoice">
              <CFormGroup className={"mt-2"}>
                <CRow>
                  <CCol md={2}>
                    <CLabel>N° de Factura:</CLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="text"
                      value={emptyValueOnUndefined(editingItem.invoiceNumber)}
                      onChange={onInvoiceNumberChange}
                      placeholder="Nro de Factura"
                    ></CInput>
                    <FieldErrors
                      errors={
                        getFieldErrors("invoiceNumber", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Proveedor:</CLabel>
                  </CCol>
                  <CCol md={10}>
                    <SupplierSelect
                      value={
                        selectedSupplier !== undefined ? selectedSupplier : null
                      }
                      onChange={onSupplierChange}
                    ></SupplierSelect>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Nombre Proveedor:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CInput
                      type="text"
                      value={emptyValueOnUndefined(editingItem.supplierName)}
                      onChange={onSupplierNameChange}
                      placeholder="Nombre del proveedor"
                      disabled={selectedSupplier !== undefined}
                    ></CInput>
                    <FieldErrors
                      errors={
                        getFieldErrors("supplierName", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <CLabel>RUC Proveedor:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CInput
                      type="text"
                      value={emptyValueOnUndefined(editingItem.supplierRuc)}
                      onChange={onSupplierRucChange}
                      placeholder="RUC proveedor"
                      disabled={selectedSupplier !== undefined}
                    ></CInput>
                    <FieldErrors
                      errors={getFieldErrors("supplierRuc", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Timbrado:</CLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="text"
                      value={emptyValueOnUndefined(editingItem.stamp)}
                      onChange={onStampChange}
                      placeholder="Timbrado"
                    ></CInput>
                    <FieldErrors
                      errors={getFieldErrors("stamp", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Condición de venta:</CLabel>
                  </CCol>
                  <CCol md={2}>
                    <div className="form-check form-check-inline">
                      <CInputRadio
                        id="saleTypeCash"
                        name="saleType"
                        className="form-check-input"
                        value={"cash"}
                        checked={editingItem.expenseInvoiceType === "cash"}
                        defaultChecked={
                          editingItem.expenseInvoiceType !== undefined &&
                          editingItem.expenseInvoiceType === "cash"
                        }
                        onChange={() => onExpenseInvoiceTypeChange("cash")}
                      ></CInputRadio>
                      <CLabel
                        htmlFor="saleTypeCash"
                        className="form-check-label"
                      >
                        {"Contado"}
                      </CLabel>
                    </div>
                    <FieldErrors
                      errors={
                        getFieldErrors("expenseInvoiceType", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <div className="form-check form-check-inline">
                      <CInputRadio
                        id="saleTypeCredit"
                        name="saleType"
                        className="form-check-input"
                        value={"credit"}
                        checked={editingItem.expenseInvoiceType === "credit"}
                        defaultChecked={
                          editingItem.expenseInvoiceType !== undefined &&
                          editingItem.expenseInvoiceType === "credit"
                        }
                        onChange={() => onExpenseInvoiceTypeChange("credit")}
                      ></CInputRadio>
                      <CLabel
                        htmlFor="saleTypeCredit"
                        className="form-check-label"
                      >
                        {"Crédito"}
                      </CLabel>
                    </div>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Gravadas 10%:</CLabel>
                  </CCol>
                  <CCol>
                    <CurrencyField
                      currency={PYG}
                      value={editingItem.iva10}
                      onChange={onIVA10Change}
                    ></CurrencyField>
                    <FieldErrors
                      errors={getFieldErrors("iva10", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <CLabel>IVA 10%:</CLabel>
                  </CCol>
                  <CCol>
                    <CurrencyField
                      currency={PYG}
                      value={(editingItem.iva10 || 0) / 11}
                      disabled
                    ></CurrencyField>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Gravadas 5%:</CLabel>
                  </CCol>
                  <CCol>
                    <CurrencyField
                      currency={PYG}
                      value={editingItem.iva5}
                      onChange={onIVA5Change}
                    ></CurrencyField>
                    <FieldErrors
                      errors={getFieldErrors("iva5", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <CLabel>IVA 5%:</CLabel>
                  </CCol>
                  <CCol>
                    <CurrencyField
                      currency={PYG}
                      value={(editingItem.iva5 || 0) / 21}
                      disabled
                    ></CurrencyField>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Exentas:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CurrencyField
                      currency={PYG}
                      value={editingItem.ivaExempt}
                      onChange={onIVAExemptChange}
                    ></CurrencyField>
                    <FieldErrors
                      errors={getFieldErrors("ivaExempt", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
            </CTabPane>
            <CTabPane data-tab="accounts">
              {editingItem.supplierName &&
                editingItem.supplierRuc &&
                !editingItem.supplierId && (
                  <CFormGroup>
                    <CRow>
                      <CCol md={3}>
                        <CLabel>
                          Proveedor escrito de forma manual. Crear?:
                        </CLabel>
                      </CCol>
                      <CCol md={1}>
                        <CButton
                          color="primary"
                          className="float-right"
                          onClick={onAddSupplierClick}
                        >
                          <BsFillPlusCircleFill />
                          &nbsp; Crear
                        </CButton>
                      </CCol>
                    </CRow>
                  </CFormGroup>
                )}
              {items.map((entry, ix) => {
                return (
                  <CRow key={entry.uuid}>
                    <CCol>
                      <AccountingEntryFormItem
                        value={entry.item}
                        onDelete={() => onEntryDelete(entry)}
                        onChange={(item) =>
                          onEntryChange({
                            uuid: entry.uuid,
                            item: item,
                          })
                        }
                        errors={ix < itemErrors.length ? itemErrors[ix] : {}}
                        initialEntry={entry.item}
                        initialAccount={entry.item?.account}
                      />
                    </CCol>
                  </CRow>
                );
              })}
              {items.length === 0 ? (
                <CRow>
                  <CCol>
                    Agregue nuevas entradas al asiento con el boton + de la
                    derecha
                  </CCol>
                </CRow>
              ) : (
                <></>
              )}
              <CRow className="mb-1">
                <CCol>
                  <CButton
                    className="btn btn-primary float-right"
                    onClick={onAddEntryClick}
                  >
                    <i className="fa fa-plus" />
                  </CButton>
                </CCol>
              </CRow>
            </CTabPane>
          </CTabContent>
        </CTabs>

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default StoreExpenseAddInvoiceForm;
